import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Skeleton } from "@mui/material";
import classNames from "classnames";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { RobotoTypography } from "@/shared/components";
import { EventRecord } from "@/shared/domain/events";
import { TopicRecord } from "@/shared/domain/topics";
import { useTopicById } from "@/shared/domain/topics/hooks/useTopic";
import { SearchParams } from "@/shared/environment";
import { nanoSecToSecStr } from "@/shared/time";

import {
  SidebarTab,
  SidebarViewType,
  useWorkspaceSidebar,
} from "../../sidebarContext";

import styles from "./Scopes.module.css";

interface AssociatedTopicProps {
  topicId: string;
  event: EventRecord;
}

export function AssociatedTopic({ topicId, event }: AssociatedTopicProps) {
  const topicQuery = useTopicById(topicId);
  const viewTopic = useViewTopic();

  if (topicQuery.isPending) {
    return <Skeleton variant="text" width="150px" />;
  }

  if (topicQuery.isError) {
    return (
      <span className={styles.container}>
        <ErrorOutlineIcon color="error" className={styles.icon} />
        <RobotoTypography component={"span"} className={styles.text}>
          Failed to load topic
        </RobotoTypography>
      </span>
    );
  }

  return (
    <span className={styles.container}>
      <AccountTreeOutlinedIcon className={styles.icon} />
      <RobotoTypography
        component={"a"}
        className={classNames(styles.text, styles.link)}
        onClick={() => viewTopic(topicQuery.data, event)}
        title={topicQuery.data.topic_name}
      >
        {topicQuery.data.topic_name}
      </RobotoTypography>
    </span>
  );
}

function useViewTopic() {
  const sidebar = useWorkspaceSidebar();
  const [_, setSearchParams] = useSearchParams();

  return React.useCallback(
    function viewTopic(topic: TopicRecord, event: EventRecord) {
      // Open sidebar with topic filter
      sidebar.openView({
        type: SidebarViewType.TabbedSidebar,
        data: {
          initialTopicFilter: {
            term: topic.topic_name,
            key: Date.now(), // Unique key for command semantics
          },
        },
      });
      sidebar.setActiveTab(SidebarTab.Topics);

      // Update timestamp in URL
      setSearchParams(
        (oldParams) => {
          const params = new URLSearchParams(oldParams);
          params.set(SearchParams.TIMESTAMP, nanoSecToSecStr(event.start_time));
          return params;
        },
        { replace: true },
      );
    },
    [sidebar, setSearchParams],
  );
}
